import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminAPI, companyAPI, serviceCommentsAPI, receiptAPI, squareAPI, bookingAPI } from '../services/api';
import Icon from './Icon';
import ReceiptUpload from './ReceiptUpload';
import SquareTicketModal from './SquareTicketModal';
import SquarePaymentModal from './SquarePaymentModal';
import SquarePendingOrders from './SquarePendingOrders';
import SquareIncomingOrders from './SquareIncomingOrders';
import BikePassportModal from './BikePassportModal';
import EnterpriseLockedOverlay from './EnterpriseLockedOverlay';
import '../styles/Admin.css';

function AdminServices({ user, onLogout, onToggleDarkMode, darkMode = true }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [services, setServices] = useState([]);
  const [bikes, setBikes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSquareTicketModal, setShowSquareTicketModal] = useState(false);
  const [ticketServiceContext, setTicketServiceContext] = useState(null);
  const [pendingTicketServiceId, setPendingTicketServiceId] = useState(null);
  const [showSquarePaymentModal, setShowSquarePaymentModal] = useState(false);
  const [squarePaymentContext, setSquarePaymentContext] = useState(null);
  const [pendingDetailServiceId, setPendingDetailServiceId] = useState(null);
  const [pendingBookingId, setPendingBookingId] = useState(null);  // When adding service from Record Service (calendar), link to booking after save
  const [needsBikeSelectionNotice, setNeedsBikeSelectionNotice] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [squareConnected, setSquareConnected] = useState(false);
  const [squareDeviceMode, setSquareDeviceMode] = useState(null); // 'terminal', 'register', or null
  const [syncingSquareInvoices, setSyncingSquareInvoices] = useState(false);
  const [bikeSearchTerm, setBikeSearchTerm] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [arrivalDateFrom, setArrivalDateFrom] = useState('');
  const [arrivalDateTo, setArrivalDateTo] = useState('');
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalServices, setTotalServices] = useState(0);
  const [servicesPages, setServicesPages] = useState(1);
  const [bikeOptionsPage, setBikeOptionsPage] = useState(1);
  const [bikeOptionsPages, setBikeOptionsPages] = useState(1);
  const [bikeOptionsTotal, setBikeOptionsTotal] = useState(0);
  const [loadingBikeOptions, setLoadingBikeOptions] = useState(false);
  const [serviceRelinkBikeSearch, setServiceRelinkBikeSearch] = useState('');
  const [serviceRelinkBikePage, setServiceRelinkBikePage] = useState(1);
  // Comment states
  const [showCommentsModal, setShowCommentsModal] = useState(null); // service id when showing comments
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commentCategory, setCommentCategory] = useState('general');
  const [loadingComments, setLoadingComments] = useState(false);
  // User details modal
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [loadingUser, setLoadingUser] = useState(false);
  // Service details modal
  const [selectedService, setSelectedService] = useState(null);
  const [showServiceDetailModal, setShowServiceDetailModal] = useState(false);
  const [serviceRelinkBikeId, setServiceRelinkBikeId] = useState('');
  const [relinkingServiceBike, setRelinkingServiceBike] = useState(false);
  // Bike Passport modal (popup with full bike page)
  const [showBikePassportModal, setShowBikePassportModal] = useState(false);
  const [bikePassportContext, setBikePassportContext] = useState(null); // { id, brand, model }
  const [sendingCompletionEmail, setSendingCompletionEmail] = useState(false);
  // Receipt upload + AI scan on existing service (detail modal)
  const [showDetailReceipt, setShowDetailReceipt] = useState(false);
  const [detailReceiptFile, setDetailReceiptFile] = useState(null);
  const [detailReceiptFiles, setDetailReceiptFiles] = useState([]);
  const [detailReceiptPreview, setDetailReceiptPreview] = useState(null);
  const [detailReceiptPreviews, setDetailReceiptPreviews] = useState([]);
  const [detailScanExtracting, setDetailScanExtracting] = useState(false);
  const [detailScanExtracted, setDetailScanExtracted] = useState(null);
  const [detailEditMode, setDetailEditMode] = useState(false);
  const [detailEditSaving, setDetailEditSaving] = useState(false);
  const [detailEditValues, setDetailEditValues] = useState({ description: '', total_cost: 0, service_date: '' });
  const [formData, setFormData] = useState({
    bike_id: '',
    service_date: new Date().toISOString().split('T')[0],
    service_time: '',
    description: '',
    total_cost: 0,
    receipt: null,
    receipts: [],  // Multiple receipts for long invoices
    service_items: []  // Extracted service items to save separately
  });

  // AI Receipt extraction states
  const [extracting, setExtracting] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [receiptPreviews, setReceiptPreviews] = useState([]);  // Previews for multiple files

  // Inline "Add new bike" state
  const [showNewBikeForm, setShowNewBikeForm] = useState(false);
  const [newBikeData, setNewBikeData] = useState({ vin: '', brand: '', model: '', year: new Date().getFullYear() });
  const [creatingBike, setCreatingBike] = useState(false);
  /** Bikes created/selected in Add Service — kept at top of picker even if API page 1 omits them */
  const pinnedAddServiceBikesRef = useRef([]);
  const squareAvailable =
    ['enterprise', 'lifetime'].includes((companyInfo?.subscription_tier || '').toLowerCase()) &&
    squareConnected;
  const squareTerminalMode = squareAvailable && squareDeviceMode === 'terminal';
  const squareRegisterMode = squareAvailable && squareDeviceMode === 'register';
  const isTechnician = user?.role === 'technician';
  // Pro/Basic: show Square section as locked overlay (except technician view)
  const tier = (companyInfo?.subscription_tier || '').toLowerCase();
  const showSquareLocked = !isTechnician && companyInfo && !['enterprise', 'lifetime'].includes(tier);
  const normalizedBikeSearchTerm = bikeSearchTerm.trim().toLowerCase();
  const filteredBikesForAdd = bikes;

  const getServiceStatusStyle = (status) => {
    const value = (status || '').toLowerCase();
    if (value === 'completed') {
      return { backgroundColor: 'rgba(76, 175, 80, 0.2)', color: '#4A8C18' };
    }
    if (value === 'pending_payment') {
      return { backgroundColor: 'rgba(245, 158, 11, 0.22)', color: '#f59e0b' };
    }
    if (value === 'ready_for_billing') {
      return { backgroundColor: 'rgba(59, 130, 246, 0.2)', color: '#60a5fa' };
    }
    if (value === 'in_progress') {
      return { backgroundColor: 'rgba(245, 158, 11, 0.2)', color: '#f59e0b' };
    }
    if (value === 'scheduled') {
      return { backgroundColor: 'rgba(148, 163, 184, 0.2)', color: '#cbd5e1' };
    }
    if (value === 'cancelled') {
      return { backgroundColor: 'rgba(239, 68, 68, 0.2)', color: '#f87171' };
    }
    return { backgroundColor: 'rgba(158, 158, 158, 0.2)', color: '#9e9e9e' };
  };

  const formatServiceStatus = (status) => {
    if (!status) return 'in progress';
    if (status === 'pending_payment') return 'pending payment';
    return status.replace(/_/g, ' ');
  };

  const formatSquareStatus = (status) => {
    if (!status) return 'unknown';
    return String(status).toLowerCase().replace(/_/g, ' ');
  };

  const shortSquareId = (value) => {
    if (!value) return '';
    if (value.length <= 14) return value;
    return `${value.slice(0, 8)}...${value.slice(-4)}`;
  };

  const isSquarePlaceholderVin = (vin) => (vin || '').toString().trim().toLowerCase().startsWith('sq-ph-');

  const formatDetailDate = (value) => {
    if (!value) return 'N/A';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return 'N/A';
    return parsed.toLocaleDateString();
  };

  const formatDetailDateTime = (value) => {
    if (!value) return 'N/A';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return 'N/A';
    return parsed.toLocaleString();
  };

  const formatSquareAmount = (amountCents, currency = 'GBP') => {
    const amount = Number(amountCents);
    if (!Number.isFinite(amount)) return 'N/A';
    const code = (currency || 'GBP').toUpperCase();
    return `${code} ${(amount / 100).toFixed(2)}`;
  };

  useEffect(() => {
    fetchCompanyInfo();
  }, []);

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, searchTerm, statusFilter, arrivalDateFrom, arrivalDateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply status filter from URL when navigating from Dashboard "Not yet invoiced"
  useEffect(() => {
    const statusParam = searchParams.get('status');
    if (statusParam && ['ready_for_billing', 'pending_payment', 'in_progress', 'completed', 'scheduled', 'cancelled'].includes(statusParam)) {
      setStatusFilter(statusParam);
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('status');
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams.get('status')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-open modals when navigated from dashboard/query params.
  useEffect(() => {
    const action = searchParams.get('action');
    if (!action) return;

    const serviceIdParam = Number(searchParams.get('serviceId'));
    const hasServiceId = Number.isFinite(serviceIdParam) && serviceIdParam > 0;

    if (action === 'add') {
      const bookingIdParam = searchParams.get('bookingId');
      const bikeIdParam = searchParams.get('bikeId');
      const bookingNeedsBikeSelection = searchParams.get('bookingNeedsBikeSelection') === '1';
      const descParam = searchParams.get('description');
      const serviceDateParam = searchParams.get('serviceDate');
      const serviceTimeParam = searchParams.get('serviceTime');
      const defaultPriceParam = searchParams.get('defaultPrice');
      if (bookingIdParam || bikeIdParam || descParam || serviceDateParam) {
        setFormData(prev => ({
          ...prev,
          ...(bikeIdParam ? { bike_id: bikeIdParam } : {}),
          ...(descParam ? { description: decodeURIComponent(descParam) } : {}),
          ...(serviceDateParam ? { service_date: serviceDateParam } : prev.service_date),
          ...(serviceTimeParam ? { service_time: serviceTimeParam } : prev.service_time),
          ...(defaultPriceParam ? { total_cost: parseFloat(defaultPriceParam) || 0 } : {})
        }));
        if (bookingIdParam) setPendingBookingId(Number(bookingIdParam));
        setNeedsBikeSelectionNotice(Boolean(bookingIdParam && bookingNeedsBikeSelection && !bikeIdParam));
      }
      setShowAddModal(true);
    }

    if (action === 'invoice') {
      if (hasServiceId) {
        const targetService = services.find((service) => service.id === serviceIdParam);
        if (targetService) {
          openTicketWizard(targetService);
        } else {
          setPendingTicketServiceId(serviceIdParam);
        }
      } else {
        openTicketWizard();
      }
    }

    if (action === 'details' && hasServiceId) {
      const targetService = services.find((service) => service.id === serviceIdParam);
      if (targetService) {
        openServiceDetails(targetService);
      } else {
        setPendingDetailServiceId(serviceIdParam);
      }
    }

    // Remove query params so this only runs once per navigation.
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('action');
    nextParams.delete('serviceId');
    nextParams.delete('bookingId');
    nextParams.delete('bikeId');
    nextParams.delete('bookingNeedsBikeSelection');
    nextParams.delete('description');
    nextParams.delete('serviceDate');
    nextParams.delete('serviceTime');
    nextParams.delete('defaultPrice');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams, services]);

  useEffect(() => {
    if (!pendingTicketServiceId) return;
    const targetService = services.find((service) => service.id === pendingTicketServiceId);
    if (targetService) {
      openTicketWizard(targetService);
      setPendingTicketServiceId(null);
      return;
    }
    loadServiceById(pendingTicketServiceId).then((loadedService) => {
      if (loadedService) {
        openTicketWizard(loadedService);
      }
      setPendingTicketServiceId(null);
    });
  }, [pendingTicketServiceId, services]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!pendingDetailServiceId) return;
    const targetService = services.find((service) => service.id === pendingDetailServiceId);
    if (targetService) {
      openServiceDetails(targetService);
      setPendingDetailServiceId(null);
      return;
    }
    loadServiceById(pendingDetailServiceId).then((loadedService) => {
      if (loadedService) {
        openServiceDetails(loadedService);
      }
      setPendingDetailServiceId(null);
    });
  }, [pendingDetailServiceId, services]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, arrivalDateFrom, arrivalDateTo, pageSize]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const servicesRes = await adminAPI.getAllServices({
        page: currentPage,
        per_page: pageSize,
        q: searchTerm.trim() || undefined,
        status: statusFilter || undefined,
        arrival_date_from: arrivalDateFrom || undefined,
        arrival_date_to: arrivalDateTo || undefined
      });

      const realServices = servicesRes.data.services || [];
      setServices(realServices);
      setTotalServices(servicesRes.data.total || 0);
      setServicesPages(servicesRes.data.pages || 1);
      setError('');
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncSquareInvoices = async () => {
    if (syncingSquareInvoices) return;
    try {
      setSyncingSquareInvoices(true);
      setError('');
      setSuccess('');
      const response = await squareAPI.syncServiceInvoices({ days: 365 });
      const data = response?.data || {};
      const matched = Number(data.matched || 0);
      const servicesUpdated = Number(data.services_updated || 0);
      const itemsAdded = Number(data.items_added || 0);
      const scanned = Number(data.orders_scanned || 0);
      const eligible = Number(data.eligible_services || 0);

      if (servicesUpdated === 0) {
        if (eligible === 0) {
          setSuccess('No services with a valid frame number were found to match against Square.');
        } else {
          setSuccess(`Scanned ${scanned} Square order${scanned === 1 ? '' : 's'} against ${eligible} service${eligible === 1 ? '' : 's'}. No frame numbers matched.`);
        }
      } else {
        setSuccess(`Square invoice sync complete: ${servicesUpdated} service${servicesUpdated === 1 ? '' : 's'} updated, ${itemsAdded} line item${itemsAdded === 1 ? '' : 's'} attached, ${matched} order match${matched === 1 ? '' : 'es'} (scanned ${scanned}).`);
      }
      await fetchData();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to sync Square invoices');
    } finally {
      setSyncingSquareInvoices(false);
    }
  };

  const fetchCompanyInfo = async () => {
    try {
      const response = await companyAPI.getCompanyInfo();
      const company = response?.data?.company || null;
      setCompanyInfo(company);

      // Only call Square status if it can be enabled and accessed from this role.
      const role = (user?.role || '').toLowerCase();
      const tier = (company?.subscription_tier || '').toLowerCase();
      const isEnterpriseTier = ['enterprise', 'lifetime'].includes(tier);
      const canAccessSquareStatus = ['company_admin', 'admin', 'super_admin'].includes(role);
      const isSquareEnabled = Boolean(company?.square_integration_enabled);

      if (isEnterpriseTier && canAccessSquareStatus && isSquareEnabled) {
        fetchSquareStatus();
      } else {
        setSquareConnected(false);
        setSquareDeviceMode(null);
      }
    } catch (err) {
      console.log('Company info not available');
      setSquareConnected(false);
      setSquareDeviceMode(null);
    }
  };

  const fetchSquareStatus = async () => {
    try {
      const response = await squareAPI.getStatus();
      const connected =
        Boolean(response?.data?.feature_available) &&
        response?.data?.connection?.status === 'connected';
      setSquareConnected(connected);
      setSquareDeviceMode(response?.data?.device_mode || response?.data?.connection?.square_device_mode || null);
    } catch (err) {
      setSquareConnected(false);
      setSquareDeviceMode(null);
    }
  };

  const mergeBikesForPicker = (apiBikes, pinned = []) => {
    const merged = [];
    const seen = new Set();
    const add = (bike) => {
      if (!bike?.id || seen.has(bike.id)) return;
      seen.add(bike.id);
      merged.push(bike);
    };
    pinned.forEach(add);
    (apiBikes || []).forEach(add);
    return merged;
  };

  const pinAddServiceBike = (bike) => {
    if (!bike?.id) return;
    pinnedAddServiceBikesRef.current = [
      bike,
      ...pinnedAddServiceBikesRef.current.filter((b) => b.id !== bike.id),
    ];
  };

  const loadBikeOptions = async ({ page = 1, q = '', forAddService = false } = {}) => {
    try {
      setLoadingBikeOptions(true);
      const response = await adminAPI.getAllBikes({
        page,
        per_page: 20,
        q: q.trim() || undefined
      });
      const fromApi = response.data.bikes || [];
      const pinned = forAddService ? pinnedAddServiceBikesRef.current : [];
      setBikes(mergeBikesForPicker(fromApi, pinned));
      setBikeOptionsPage(response.data.page || page);
      setBikeOptionsPages(response.data.pages || 1);
      setBikeOptionsTotal(response.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load bikes');
    } finally {
      setLoadingBikeOptions(false);
    }
  };

  const loadServiceById = async (serviceId) => {
    try {
      const response = await adminAPI.getAllServices({ service_id: serviceId, page: 1, per_page: 1 });
      return response.data.services?.[0] || null;
    } catch (err) {
      return null;
    }
  };

  useEffect(() => {
    if (!showAddModal || showNewBikeForm) return;
    loadBikeOptions({ page: bikeOptionsPage, q: bikeSearchTerm, forAddService: true });
  }, [showAddModal, showNewBikeForm, bikeOptionsPage, bikeSearchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showServiceDetailModal) return;
    loadBikeOptions({ page: serviceRelinkBikePage, q: serviceRelinkBikeSearch });
  }, [showServiceDetailModal, serviceRelinkBikePage, serviceRelinkBikeSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUserClick = async (userId) => {
    if (!userId) return;
    setLoadingUser(true);
    setShowUserModal(true);
    try {
      const response = await adminAPI.getUser(userId);
      setSelectedUser(response.data.user);
    } catch (err) {
      setError('Failed to load user details');
      setShowUserModal(false);
    } finally {
      setLoadingUser(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const parsedTotalCost = Number(formData.total_cost);
    if (Number.isNaN(parsedTotalCost) || parsedTotalCost < 0) {
      setError('Total cost cannot be negative.');
      return;
    }

    // Bike is required - show friendly error if missing
    if (!formData.bike_id || formData.bike_id === '') {
      let hint = ' Select a bike from the list, or create one using "+ Add New Bike".';
      if (showNewBikeForm) {
        hint = ' Click "Create Bike & Select" first to save the bike (VIN is required), then Add Service.';
      } else if (extractedData?.frame_number) {
        hint = ` The receipt extracted frame number: ${extractedData.frame_number}. Create a new bike with these details, or select an existing one.`;
      }
      setError('Please select a bike or create one first.' + hint);
      return;
    }

    const data = new FormData();
    data.append('bike_id', formData.bike_id);
    data.append('service_date', formData.service_date);
    if (formData.service_time) {
      data.append('service_time', formData.service_time);
    }
    data.append('description', formData.description);
    data.append('total_cost', formData.total_cost);

    // Single receipt file
    if (formData.receipt) {
      data.append('receipt', formData.receipt);
    }

    // Multiple receipt files
    if (formData.receipts && formData.receipts.length > 0) {
      formData.receipts.forEach((file, index) => {
        data.append('receipts[]', file);
      });
    }

    // Service items (extracted from receipt) - send as JSON
    if (formData.service_items && formData.service_items.length > 0) {
      data.append('service_items', JSON.stringify(formData.service_items));
    }

    try {
      const response = await adminAPI.addService(data);
      const newService = response?.data?.service;
      const itemsCount = formData.service_items?.length || 0;
      const itemsMsg = itemsCount > 0 ? ` with ${itemsCount} item(s)` : '';
      if (pendingBookingId && newService?.id) {
        try {
          await bookingAPI.updateBookingStatus(pendingBookingId, 'completed', newService.id);
          setSuccess(`Service added and booking completed!`);
          setPendingBookingId(null);
        } catch (bookingErr) {
          setSuccess(`Service added${itemsMsg}! (Booking link failed: ${bookingErr?.response?.data?.error || 'Unknown'})`);
          setPendingBookingId(null);
        }
      } else {
        setSuccess(`Service added successfully${itemsMsg}!`);
      }
      setShowAddModal(false);
      resetAddForm();
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add service');
    }
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service record?')) {
      return;
    }

    try {
      await adminAPI.deleteService(serviceId);
      setSuccess('Service deleted successfully!');
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to delete service');
    }
  };

  // Comment functions
  const fetchComments = async (serviceId) => {
    setLoadingComments(true);
    try {
      const res = await serviceCommentsAPI.getComments(serviceId);
      setComments(res.data.comments || []);
    } catch (err) {
      console.error('Failed to load comments', err);
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleOpenComments = async (serviceId) => {
    setShowCommentsModal(serviceId);
    setNewComment('');
    setCommentCategory('general');
    await fetchComments(serviceId);
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      await serviceCommentsAPI.addComment(showCommentsModal, {
        comment_text: newComment,
        category: commentCategory
      });
      setNewComment('');
      setSuccess('Comment added!');
      await fetchComments(showCommentsModal);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add comment');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    try {
      await serviceCommentsAPI.deleteComment(showCommentsModal, commentId);
      await fetchComments(showCommentsModal);
      setSuccess('Comment deleted!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete comment');
    }
  };

  // Check if a file is an image (handles iOS edge cases where type may be empty)
  const isImageFile = (file) => {
    if (file.type && file.type.startsWith('image/')) return true;
    const ext = (file.name || '').split('.').pop().toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'bmp', 'tiff'].includes(ext);
  };

  // Handle single receipt file selection
  const handleReceiptSelect = (file) => {
    setFormData({ ...formData, receipt: file, receipts: [] });
    setExtractedData(null);
    setReceiptPreviews([]);
    if (file && isImageFile(file)) {
      const reader = new FileReader();
      reader.onloadend = () => setReceiptPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setReceiptPreview(null);
    }
  };

  // Handle multiple receipt file selection
  const handleReceiptsSelect = (files) => {
    setFormData({ ...formData, receipt: null, receipts: files });
    setExtractedData(null);
    setReceiptPreview(null);

    // Generate previews for all files
    files.forEach((file, index) => {
      if (isImageFile(file)) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setReceiptPreviews(prev => {
            const updated = [...prev];
            updated[index] = reader.result;
            return updated;
          });
        };
        reader.readAsDataURL(file);
      }
    });
  };

  // AI Extract data from receipt(s)
  const handleAIExtract = async () => {
    const hasMultipleFiles = formData.receipts && formData.receipts.length > 0;
    const hasSingleFile = formData.receipt;

    if (!hasMultipleFiles && !hasSingleFile) return;

    setExtracting(true);
    setError('');
    setSuccess('');

    try {
      let extracted;

      if (hasMultipleFiles) {
        // Use batch extraction for multiple files
        const response = await receiptAPI.extractFromBatch(formData.receipts);
        extracted = response.data.extracted_data;

        if (response.data.errors && response.data.errors.length > 0) {
          console.warn('Some files had extraction issues:', response.data.errors);
          // Show partial error to user
          if (response.data.errors.length === formData.receipts.length) {
            setError('AI could not read any of the uploaded images. Please try with clearer photos.');
            setExtracting(false);
            return;
          }
        }
      } else {
        // Single file extraction
        const formDataUpload = new FormData();
        formDataUpload.append('receipt', formData.receipt);

        const response = await receiptAPI.extractFromImage(formDataUpload);
        extracted = response.data.extracted_data;
      }

      // Check if extraction returned meaningful data
      const hasItems = extracted?.service_items && extracted.service_items.length > 0;
      const hasTotal = extracted?.total_cost && extracted.total_cost > 0;
      const hasDescription = extracted?.description && extracted.description.trim().length > 0;
      const hasAnyData = hasItems || hasTotal || hasDescription;

      if (!extracted || !hasAnyData) {
        // Extraction completed but found nothing useful
        setExtractedData(null);
        setError(
          'AI could not extract any data from this receipt. This can happen if:\n' +
          '- The photo is blurry or has glare/reflections\n' +
          '- The receipt text is too small or hard to read\n' +
          'Please try taking a clearer photo with good lighting.'
        );
        setExtracting(false);
        return;
      }

      setExtractedData(extracted);

      // Auto-fill form fields with extracted data
      // Description = service type/description (NOT the items list - items go into service_items table)
      let description = extracted.description || '';

      // Parse date if it's a string
      let serviceDate = formData.service_date;
      if (extracted.service_date) {
        const dateStr = extracted.service_date;
        const parsedDate = new Date(dateStr);
        if (!isNaN(parsedDate.getTime())) {
          serviceDate = parsedDate.toISOString().split('T')[0];
        }
      }

      // Store service items separately for saving to database
      const serviceItems = (extracted.service_items || []).map(item => ({
        item_name: item.description,
        quantity: item.quantity || 1,
        unit_price: item.unit_price || item.total || 0,
        total_price: item.total || (item.unit_price * (item.quantity || 1)) || 0
      }));

      // Bike matching: if extraction has frame_number, try to find existing bike or pre-fill "Add new bike"
      let matchedBikeId = '';
      const frameNumber = (extracted.frame_number || '').toString().trim();
      const bikeMake = (extracted.bike_make || extracted.brand || '').toString().trim();
      const bikeModel = (extracted.bike_model || extracted.model || '').toString().trim();

      if (frameNumber && bikes.length > 0) {
        const normalized = frameNumber.toLowerCase();
        const found = bikes.find(b => (b.vin || '').toLowerCase() === normalized);
        if (found) {
          matchedBikeId = String(found.id);
        }
      }

      // Pre-fill new bike form when extraction has bike info but no match
      if (!matchedBikeId && (frameNumber || bikeMake || bikeModel)) {
        setNewBikeData(prev => ({
          vin: frameNumber || prev.vin,
          brand: bikeMake || prev.brand,
          model: bikeModel || prev.model,
          year: prev.year || new Date().getFullYear()
        }));
        setShowNewBikeForm(true);
      }

      setFormData(prev => ({
        ...prev,
        description: description || prev.description,
        total_cost: extracted.total_cost || prev.total_cost,
        service_date: serviceDate,
        service_items: serviceItems,
        bike_id: matchedBikeId || prev.bike_id
      }));

      // Build a detailed success message
      const parts = [];
      if (hasItems) parts.push(`${extracted.service_items.length} item(s)`);
      if (hasTotal) parts.push(`total: ${extracted.currency || '£'}${extracted.total_cost.toFixed(2)}`);
      if (extracted.service_date) parts.push(`date: ${extracted.service_date}`);
      if (frameNumber) parts.push(`frame: ${frameNumber}`);
      const filesInfo = hasMultipleFiles ? ` from ${formData.receipts.length} image(s)` : '';
      let successMsg = `Extracted${filesInfo}: ${parts.join(', ')}. Review below and adjust if needed.`;
      if (matchedBikeId) successMsg += ' Bike matched and selected.';
      else if (frameNumber || bikeMake || bikeModel) successMsg += ' Bike form pre-filled – create bike or select existing, then Add Service.';
      setSuccess(successMsg);
      setTimeout(() => setSuccess(''), 8000);
    } catch (err) {
      console.error('AI extraction error:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to extract data from receipt';
      if (err.code === 'ECONNABORTED' || errorMsg.includes('timeout')) {
        setError('Extraction timed out. The image might be too large. Please try with a smaller or clearer photo.');
      } else {
        setError(errorMsg);
      }
    } finally {
      setExtracting(false);
    }
  };

  // Create a new bike inline and auto-select it
  const handleCreateNewBike = async () => {
    if (!newBikeData.vin || !newBikeData.brand || !newBikeData.model || !newBikeData.year) {
      setError('All bike fields (VIN, brand, model, year) are required');
      return;
    }
    setCreatingBike(true);
    setError('');
    const trySelectExistingByVin = async (vinRaw) => {
      const q = (vinRaw || '').trim();
      if (!q) return false;
      const norm = (v) => (v || '').toString().trim().toLowerCase();
      const want = norm(q);
      try {
        const searchRes = await adminAPI.getAllBikes({
          page: 1,
          per_page: 50,
          q,
        });
        const list = searchRes.data?.bikes || [];
        const exact =
          list.find((b) => norm(b.vin) === want) ||
          list.find((b) => norm(b.vin).includes(want) || want.includes(norm(b.vin))) ||
          list[0];
        if (!exact) return false;
        setError('');
        pinAddServiceBike(exact);
        setFormData((prev) => ({ ...prev, bike_id: String(exact.id) }));
        setBikeSearchTerm(exact.vin || q);
        setBikeOptionsPage(1);
        setShowNewBikeForm(false);
        setNewBikeData({ vin: '', brand: '', model: '', year: new Date().getFullYear() });
        await loadBikeOptions({ page: 1, q: exact.vin || q, forAddService: true });
        setSuccess(
          'This frame number is already in your workshop — that bike is now selected. Add the service below (you can log many services on the same bike).'
        );
        setTimeout(() => setSuccess(''), 6000);
        return true;
      } catch {
        return false;
      }
    };

    try {
      const response = await adminAPI.addBike(newBikeData);
      const createdBike = response.data.bike;
      pinAddServiceBike(createdBike);
      setFormData((prev) => ({ ...prev, bike_id: String(createdBike.id) }));
      setBikeSearchTerm(createdBike.vin || newBikeData.vin);
      setBikeOptionsPage(1);
      setShowNewBikeForm(false);
      setNewBikeData({ vin: '', brand: '', model: '', year: new Date().getFullYear() });
      await loadBikeOptions({
        page: 1,
        q: createdBike.vin || newBikeData.vin,
        forAddService: true,
      });
      setSuccess('Bike created and selected — it appears at the top of the list.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      const status = err.response?.status;
      const errMsg = err.response?.data?.error || err.message || 'Failed to create bike';
      const duplicateVin =
        status === 409 ||
        /already exists|vin already|this vin/i.test(String(errMsg));
      if (duplicateVin && (await trySelectExistingByVin(newBikeData.vin))) {
        // Recovered: existing bike selected
      } else if (duplicateVin) {
        setError(
          `${errMsg} Use the bike search field (frame number) above after closing "New Bike", or pick the bike from the list — then add another service for that same passport.`
        );
      } else {
        setError(errMsg);
      }
    } finally {
      setCreatingBike(false);
    }
  };

  // Reset form when modal closes
  const resetAddForm = () => {
    setFormData({
      bike_id: '',
      service_date: new Date().toISOString().split('T')[0],
      service_time: '',
      description: '',
      total_cost: 0,
      receipt: null,
      receipts: [],
      service_items: []
    });
    setExtractedData(null);
    setReceiptPreview(null);
    setReceiptPreviews([]);
    setShowNewBikeForm(false);
    setNewBikeData({ vin: '', brand: '', model: '', year: new Date().getFullYear() });
    setPendingBookingId(null);
    setNeedsBikeSelectionNotice(false);
    setBikeSearchTerm('');
    setBikeOptionsPage(1);
    pinnedAddServiceBikesRef.current = [];
  };

  const handleSquareTicketSaved = (result) => {
    setShowSquareTicketModal(false);
    setTicketServiceContext(null);
    if (result?.ticketSaved) {
      setSuccess('Service ticket created. It will appear as "In Progress" in your services list.');
    } else {
      setSuccess('Ticket created successfully.');
    }
    fetchData();
    setTimeout(() => setSuccess(''), 4000);
  };

  const handleSquarePaymentDone = () => {
    setShowSquarePaymentModal(false);
    setSquarePaymentContext(null);
    setSuccess('Payment processed successfully.');
    fetchData();
    setTimeout(() => setSuccess(''), 4000);
  };

  const openTicketWizard = (service = null) => {
    setTicketServiceContext(service || null);
    setShowSquareTicketModal(true);
  };

  const openPaymentModal = (service) => {
    setSquarePaymentContext({
      orderId: service.square_session?.square_order_id,
      serviceId: service.id,
      amountCents: Math.round((service.total_cost || 0) * 100),
      currency: service.square_session?.currency || 'GBP',
      frameNumber: service.bike_vin || service.frame_number || '',
      locationId: service.square_session?.square_location_id,
      ticketName: service.square_session?.ticket_name || '',
    });
    setShowSquarePaymentModal(true);
  };

  const openServiceDetails = (service) => {
    setSelectedService(service);
    setServiceRelinkBikeId('');
    setServiceRelinkBikeSearch('');
    setServiceRelinkBikePage(1);
    setShowDetailReceipt(false);
    setDetailReceiptFile(null);
    setDetailReceiptFiles([]);
    setDetailReceiptPreview(null);
    setDetailReceiptPreviews([]);
    setDetailScanExtracted(null);
    setDetailScanExtracting(false);
    setDetailEditMode(false);
    const sd = service?.service_date;
    const dateStr = !sd ? '' : typeof sd === 'string' ? sd.split('T')[0] : (sd instanceof Date ? sd.toISOString().split('T')[0] : String(sd).split('T')[0]);
    setDetailEditValues({
      description: service?.description || '',
      total_cost: service?.total_cost ?? 0,
      service_date: dateStr
    });
    setShowServiceDetailModal(true);
  };

  const handleDetailAIExtract = async () => {
    const hasMultiple = detailReceiptFiles.length > 0;
    const hasSingle = detailReceiptFile;
    if (!hasMultiple && !hasSingle) return;

    setDetailScanExtracting(true);
    setDetailScanExtracted(null);
    setError('');

    try {
      let extracted;
      if (hasMultiple) {
        const res = await receiptAPI.extractFromBatch(detailReceiptFiles);
        extracted = res.data.extracted_data;
      } else {
        const fd = new FormData();
        fd.append('receipt', detailReceiptFile);
        const res = await receiptAPI.extractFromImage(fd);
        extracted = res.data.extracted_data;
      }

      const hasItems = extracted?.service_items?.length > 0;
      const hasTotal = extracted?.total_cost > 0;
      const hasDesc = extracted?.description?.trim().length > 0;

      if (!extracted || !(hasItems || hasTotal || hasDesc)) {
        setError('AI could not extract data from this receipt. Try a clearer photo.');
        setDetailScanExtracting(false);
        return;
      }

      setDetailScanExtracted(extracted);
      const parts = [];
      if (hasItems) parts.push(`${extracted.service_items.length} item(s)`);
      if (hasTotal) parts.push(`total: ${extracted.currency || '£'}${extracted.total_cost.toFixed(2)}`);
      setSuccess(`Extracted: ${parts.join(', ')}. Review below and apply if correct.`);
      setTimeout(() => setSuccess(''), 6000);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'AI extraction failed';
      if (err.code === 'ECONNABORTED' || msg.includes('timeout')) {
        setError('Extraction timed out. Try a smaller or clearer photo.');
      } else {
        setError(msg);
      }
      setTimeout(() => setError(''), 5000);
    } finally {
      setDetailScanExtracting(false);
    }
  };

  const handleDetailReceiptSave = async () => {
    const files = detailReceiptFiles.length > 0 ? detailReceiptFiles : (detailReceiptFile ? [detailReceiptFile] : []);
    if (!files.length || !selectedService?.id) return;

    try {
      setError('');
      const fd = new FormData();
      files.forEach(f => fd.append('receipts[]', f));
      const res = await adminAPI.uploadServiceReceipts(selectedService.id, fd);

      if (detailScanExtracted) {
        const updates = {};
        if (detailScanExtracted.description) updates.description = detailScanExtracted.description;
        if (detailScanExtracted.total_cost > 0) updates.total_cost = detailScanExtracted.total_cost;
        if (Object.keys(updates).length > 0) {
          await adminAPI.updateService(selectedService.id, updates);
        }
      }

      setSuccess(detailScanExtracted ? 'Receipt saved & extracted data applied' : 'Receipt(s) uploaded');
      setShowDetailReceipt(false);
      setDetailReceiptFile(null);
      setDetailReceiptFiles([]);
      setDetailReceiptPreview(null);
      setDetailReceiptPreviews([]);
      setDetailScanExtracted(null);
      fetchData();
      if (res.data?.service) {
        setSelectedService(res.data.service);
      } else {
        const updated = await loadServiceById(selectedService.id);
        if (updated) setSelectedService(updated);
      }
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save receipt');
      setTimeout(() => setError(''), 4000);
    }
  };

  const handleRelinkServiceBike = async () => {
    if (!selectedService?.id) return;
    const nextBikeId = Number(serviceRelinkBikeId);
    if (!Number.isFinite(nextBikeId) || nextBikeId <= 0) {
      setError('Please select a bike passport first.');
      return;
    }

    setRelinkingServiceBike(true);
    try {
      const response = await adminAPI.updateService(selectedService.id, { bike_id: nextBikeId });
      const updatedService = response?.data?.service;
      if (updatedService) {
        setSelectedService(updatedService);
      }
      setSuccess('Service linked to selected bike passport.');
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to link service to bike passport');
    } finally {
      setRelinkingServiceBike(false);
    }
  };

  if (loading && services.length === 0) {
    return <div className="loading">Loading services...</div>;
  }

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2 style={{ color: '#ffffff' }}>Service Management</h2>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {squareAvailable && (user?.role === 'company_admin' || user?.role === 'admin' || user?.role === 'super_admin') && (
            <button
              onClick={handleSyncSquareInvoices}
              disabled={syncingSquareInvoices}
              className="btn btn-secondary"
              title="Scan recent Square invoices and attach line items to services with matching frame numbers"
              style={{
                opacity: syncingSquareInvoices ? 0.6 : 1,
                cursor: syncingSquareInvoices ? 'not-allowed' : 'pointer',
              }}
            >
              {syncingSquareInvoices ? 'Syncing Square invoices...' : 'Sync Square invoices'}
            </button>
          )}
          {squareTerminalMode && (
            <button onClick={() => openTicketWizard()} className="btn btn-secondary">
              Create service ticket
            </button>
          )}
          {(user?.role === 'company_admin' || user?.role === 'admin' || user?.role === 'super_admin' || (user?.role === 'technician' && companyInfo?.technician_can_add_services !== false)) && (
            <button onClick={() => setShowAddModal(true)} className="btn btn-primary">
              + Add New Service
            </button>
          )}
        </div>
      </div>

      {/* Square integration: Enterprise content or locked overlay for Pro/Basic */}
      {showSquareLocked ? (
        <div style={{ marginBottom: '24px' }}>
          <EnterpriseLockedOverlay
            title="Square Integration"
            description="Sync Square tickets, create service work orders, and link bikes to incoming Square tickets."
            darkMode={darkMode}
          />
        </div>
      ) : (
        <>
          {/* Register mode: show incoming Square orders for frame number linking */}
          {(squareRegisterMode || squareTerminalMode) && (
            <SquareIncomingOrders darkMode={darkMode} />
          )}

          {/* Terminal mode: also show pending orders */}
          {squareTerminalMode && (
            <SquarePendingOrders darkMode={darkMode} />
          )}
        </>
      )}

      {companyInfo && (
        <div style={{ marginBottom: '15px', padding: '12px 16px', backgroundColor: 'rgba(61, 90, 32, 0.08)', border: '1px solid rgba(61, 90, 32, 0.2)', borderRadius: '12px', fontSize: '14px', color: 'rgba(255,255,255,0.8)' }}>
          <strong style={{ color: '#3d5a20' }}>Company:</strong> {companyInfo.name}
          {companyInfo.email && <span> | {companyInfo.email}</span>}
          {companyInfo.phone && <span> | {companyInfo.phone}</span>}
        </div>
      )}

      <div style={{ marginBottom: '15px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search by frame number, brand, customer name, or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            maxWidth: '400px',
            padding: '12px 16px',
            borderRadius: '12px',
            border: '1px solid rgba(255,255,255,0.1)',
            background: 'var(--bg-overlay-medium)',
            color: '#ffffff',
            fontSize: '14px'
          }}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: '12px 16px',
            borderRadius: '12px',
            border: '1px solid rgba(255,255,255,0.1)',
            background: 'var(--bg-overlay-medium)',
            color: '#ffffff',
            fontSize: '14px',
            cursor: 'pointer',
          }}
        >
          <option value="">Any status</option>
          <option value="in_progress">In Progress</option>
          <option value="ready_for_billing">Ready for Billing</option>
          <option value="pending_payment">Pending Payment</option>
          <option value="completed">Completed</option>
          <option value="scheduled">Scheduled</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>Arrival:</span>
          <input
            type="date"
            value={arrivalDateFrom}
            onChange={(e) => setArrivalDateFrom(e.target.value)}
            placeholder="From"
            style={{
              padding: '10px 12px',
              borderRadius: '10px',
              border: '1px solid rgba(255,255,255,0.1)',
              background: 'var(--bg-overlay-medium)',
              color: '#ffffff',
              fontSize: '13px',
            }}
          />
          <span style={{ color: 'rgba(255,255,255,0.4)' }}>–</span>
          <input
            type="date"
            value={arrivalDateTo}
            onChange={(e) => setArrivalDateTo(e.target.value)}
            placeholder="To"
            style={{
              padding: '10px 12px',
              borderRadius: '10px',
              border: '1px solid rgba(255,255,255,0.1)',
              background: 'var(--bg-overlay-medium)',
              color: '#ffffff',
              fontSize: '13px',
            }}
          />
        </div>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      <div className="card">
        {services.length === 0 ? (
          <p style={{ padding: '20px', textAlign: 'center', color: 'var(--color-gray)' }}>
            {(searchTerm || statusFilter || arrivalDateFrom || arrivalDateTo) ? 'No services found matching your filters.' : 'No services found.'}
          </p>
        ) : (
          <div className="admin-table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>User Surname</th>
                  <th>Bike Brand</th>
                  <th>Frame Number</th>
                  <th>Description</th>
                  <th>Cost</th>
                  <th>Status</th>
                  <th>Receipt</th>
                  {companyInfo && <th>Company</th>}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {services.map(service => {
                  const isUpcoming = (service.status || '').toLowerCase() === 'scheduled';
                  return (
                  <tr
                    key={service.id}
                    onClick={() => openServiceDetails(service)}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: isUpcoming ? 'rgba(255,255,255,0.06)' : undefined,
                    }}
                  >
                    <td data-label="Date">
                      {new Date(service.service_date).toLocaleDateString()}
                    </td>
                    <td data-label="User">
                      {service.user_id ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <span>{service.user_surname || 'N/A'}</span>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleUserClick(service.user_id); }}
                            style={{
                              background: 'none', border: 'none', padding: '2px', cursor: 'pointer',
                              color: 'rgba(33, 150, 243, 0.7)', display: 'inline-flex', alignItems: 'center'
                            }}
                            title="View user details"
                          >
                            <Icon name="profile" color="info" darkMode={true} size={12} />
                          </button>
                        </span>
                      ) : (
                        service.user_surname || 'N/A'
                      )}
                    </td>
                    <td data-label="Bike">{service.bike_brand || 'N/A'}</td>
                    <td data-label="Frame #">{service.bike_vin}</td>
                    <td data-label="Description" style={{ maxWidth: '200px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                          {service.description || 'N/A'}
                        </span>
                        {service.is_locked !== false && !['scheduled', 'in_progress', 'ready_for_billing', 'pending_payment'].includes((service.status || '').toLowerCase()) && (
                          <Icon name="lock" color="muted" darkMode={true} size={14} title="Locked - cannot be modified" />
                        )}
                      </div>
                    </td>
                    <td data-label="Cost">£{service.total_cost?.toFixed(2) || '0.00'}</td>
                    <td data-label="Status">
                      {(() => {
                        const statusStyle = getServiceStatusStyle(service.status);
                        const squareSession = service.square_session;
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              backgroundColor: statusStyle.backgroundColor,
                              color: statusStyle.color,
                              textTransform: 'capitalize',
                              width: 'fit-content'
                            }}>
                              {formatServiceStatus(service.status)}
                            </span>
                            {squareSession?.square_order_id && (
                              <span style={{ fontSize: '10px', color: 'rgba(96, 165, 250, 0.9)' }}>
                                SQ: {shortSquareId(squareSession.square_order_id)} ({formatSquareStatus(squareSession.payment_status)})
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td data-label="Receipt">
                      {service.receipts && service.receipts.length > 0 ? (
                        <span style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {service.receipts.map((r, idx) => (
                            r.file_url ? (
                              <a
                                key={r.id || idx}
                                href={r.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                style={{ color: '#2196f3', fontSize: '12px' }}
                              >
                                {r.original_filename || r.filename || `Receipt ${idx + 1}`}
                              </a>
                            ) : (
                              <span key={r.id || idx} style={{ fontSize: '12px' }}>Receipt {idx + 1}</span>
                            )
                          ))}
                        </span>
                      ) : service.receipt_path ? (
                        <span style={{ color: 'var(--color-gray)' }}>Legacy</span>
                      ) : (
                        'No'
                      )}
                    </td>
                    {companyInfo && (
                      <td data-label="Company" style={{ fontSize: '12px', color: 'var(--color-gray)' }}>
                        {companyInfo.name}
                      </td>
                    )}
                    <td data-label="Actions">
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openServiceDetails(service);
                          }}
                          className="btn btn-sm"
                          style={{ padding: '2px 8px', fontSize: '11px', backgroundColor: 'rgba(61, 90, 32, 0.2)', color: '#4A8C18' }}
                          title="View details"
                        >
                          View
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenComments(service.id);
                          }}
                          className="btn btn-sm"
                          style={{ padding: '2px 8px', fontSize: '11px', backgroundColor: 'rgba(33, 150, 243, 0.2)', color: '#2196f3', display: 'flex', alignItems: 'center', gap: '4px' }}
                          title="View / add comments"
                        >
                          <Icon name="chat" color="info" darkMode={true} size={12} />
                          {service.comment_count > 0 ? service.comment_count : ''}
                        </button>
                        {squareTerminalMode && service.status !== 'completed' && service.status !== 'cancelled' && (service.status === 'ready_for_billing' || service.status === 'pending_payment') && service.square_session?.square_order_id && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openPaymentModal(service);
                            }}
                            className="btn btn-sm"
                            style={{
                              padding: '2px 8px',
                              fontSize: '11px',
                              backgroundColor: 'rgba(59, 130, 246, 0.2)',
                              color: '#60a5fa',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                            title="Process payment for this service"
                          >
                            <Icon name="payment" color="info" darkMode={true} size={12} />
                            Pay
                          </button>
                        )}
                        {squareTerminalMode && service.status === 'pending_payment' && (
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                setError('');
                                const res = await squareAPI.createCheckoutLinkForService(service.id);
                                const data = res?.data || res || {};
                                if (data.already_paid) {
                                  setSuccess('This order was already paid in Square! Refreshing...');
                                  fetchData();
                                  setTimeout(() => setSuccess(''), 3000);
                                  return;
                                }
                                if (data.url) {
                                  window.open(data.url, '_blank');
                                } else {
                                  setError('Checkout link was created but no URL was returned.');
                                }
                              } catch (err) {
                                setError(err.response?.data?.error || 'Failed to create checkout link');
                                setTimeout(() => setError(''), 4000);
                              }
                            }}
                            className="btn btn-sm"
                            style={{
                              padding: '2px 8px',
                              fontSize: '11px',
                              backgroundColor: 'rgba(245, 158, 11, 0.25)',
                              color: '#f59e0b',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontWeight: 600,
                            }}
                            title="Open Square checkout page to process payment"
                          >
                            <Icon name="payment" color="warning" darkMode={true} size={12} />
                            Checkout Square
                          </button>
                        )}
                        {service.square_session?.square_order_id && (
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await navigator.clipboard.writeText(service.square_session.square_order_id);
                                setSuccess('Square order ID copied.');
                                setTimeout(() => setSuccess(''), 2500);
                              } catch (copyError) {
                                setError('Could not copy Square order ID.');
                                setTimeout(() => setError(''), 2500);
                              }
                            }}
                            className="btn btn-sm"
                            style={{
                              padding: '2px 8px',
                              fontSize: '11px',
                              backgroundColor: 'rgba(96, 165, 250, 0.18)',
                              color: '#93c5fd'
                            }}
                            title={`Copy Square order ID (${service.square_session.square_order_id})`}
                          >
                            Square ID
                          </button>
                        )}
                        {user?.role === 'super_admin' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(service.id);
                            }}
                            className="btn btn-danger btn-sm"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {totalServices > 0 && (
          <div style={{
            padding: '10px',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '10px',
            flexWrap: 'wrap',
            fontSize: '12px',
            color: 'var(--color-gray)'
          }}>
            <div>
              Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalServices)} of {totalServices}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label htmlFor="services-page-size">Per page</label>
              <select
                id="services-page-size"
                value={pageSize}
                onChange={(e) => {
                  setCurrentPage(1);
                  setPageSize(Number(e.target.value));
                }}
                style={{ padding: '6px 8px', borderRadius: '8px', fontSize: '12px' }}
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage <= 1}
              >
                Prev
              </button>
              <span>Page {currentPage}/{servicesPages}</span>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => setCurrentPage(Math.min(servicesPages, currentPage + 1))}
                disabled={currentPage >= servicesPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
        {(searchTerm || statusFilter || arrivalDateFrom || arrivalDateTo) && totalServices > 0 && (
          <div style={{ padding: '10px', fontSize: '12px', color: 'var(--color-gray)', textAlign: 'center' }}>
            Filtered total: {totalServices} services
          </div>
        )}
      </div>

      {/* Add Service Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => { setShowAddModal(false); resetAddForm(); }}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Service</h2>
              <button
                type="button"
                className="close-btn"
                aria-label="Close"
                onClick={() => { setShowAddModal(false); resetAddForm(); }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleAdd} noValidate>
              {error && (
                <div className="error" role="alert" style={{ marginBottom: '16px', padding: '12px', borderRadius: '8px', background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.4)', color: '#f87171', fontWeight: '600' }}>
                  {error}
                </div>
              )}
              <div className="form-group">
                <label>Bike *</label>
                {needsBikeSelectionNotice && !formData.bike_id && (
                  <div
                    role="status"
                    style={{
                      marginBottom: '10px',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid rgba(59, 130, 246, 0.45)',
                      background: 'rgba(59, 130, 246, 0.12)',
                      color: '#93c5fd',
                      fontSize: '13px',
                      fontWeight: '600'
                    }}
                  >
                    This booking was imported without a valid bike frame number. Select an existing bike passport or create a new bike before saving the service.
                  </div>
                )}
                {!showNewBikeForm ? (
                  <div className="service-bike-picker">
                    <label htmlFor="add-service-bike-search" className="service-bike-picker__search-label">
                      Search bike
                    </label>
                    <input
                      id="add-service-bike-search"
                      type="text"
                      className="bike-search-input"
                      inputMode="search"
                      autoComplete="off"
                      autoCorrect="off"
                      spellCheck={false}
                      value={bikeSearchTerm}
                      onChange={(e) => {
                        setBikeSearchTerm(e.target.value);
                        setBikeOptionsPage(1);
                      }}
                      placeholder="Search bike by frame number, brand, model or owner"
                    />
                    <p className="service-bike-picker__hint">
                      Tip: type the frame number to find an existing passport — you can add unlimited services on the same bike without creating a duplicate entry.
                    </p>
                    <label htmlFor="add-service-bike-select" className="service-bike-picker__select-label">
                      Select bike
                    </label>
                    <select
                      id="add-service-bike-select"
                      className="service-bike-picker__select"
                      value={formData.bike_id}
                      onChange={(e) => setFormData({ ...formData, bike_id: e.target.value })}
                      required
                    >
                      <option value="">Select a bike</option>
                      {filteredBikesForAdd.map(bike => (
                        <option key={bike.id} value={bike.id}>
                          {bike.vin} - {bike.brand} {bike.model} ({bike.year})
                          {bike.owner_surname && ` - ${bike.owner_surname} ${bike.owner_firstname || ''}`}
                          {companyInfo && ` [${companyInfo.name}]`}
                        </option>
                      ))}
                    </select>
                    {bikeOptionsTotal > 0 && (
                      <div style={{
                        marginTop: '8px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '8px',
                        flexWrap: 'wrap',
                        fontSize: '12px',
                        color: 'var(--color-gray)'
                      }}>
                        <span>
                          {loadingBikeOptions ? 'Loading bikes...' : `Showing ${((bikeOptionsPage - 1) * 20) + 1}-${Math.min(bikeOptionsPage * 20, bikeOptionsTotal)} of ${bikeOptionsTotal}`}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => setBikeOptionsPage(Math.max(1, bikeOptionsPage - 1))}
                            disabled={bikeOptionsPage <= 1 || loadingBikeOptions}
                          >
                            Prev
                          </button>
                          <span>Page {bikeOptionsPage}/{bikeOptionsPages}</span>
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => setBikeOptionsPage(Math.min(bikeOptionsPages, bikeOptionsPage + 1))}
                            disabled={bikeOptionsPage >= bikeOptionsPages || loadingBikeOptions}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                    {normalizedBikeSearchTerm && filteredBikesForAdd.length === 0 && (
                      <p style={{ fontSize: '12px', color: 'var(--color-gray)', marginTop: '6px' }}>
                        No bike found for "{bikeSearchTerm}".
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() => setShowNewBikeForm(true)}
                      style={{
                        marginTop: '8px',
                        padding: '8px 14px',
                        border: '1px dashed rgba(74, 140, 24, 0.5)',
                        borderRadius: '10px',
                        background: 'rgba(74, 140, 24, 0.06)',
                        color: '#4A8C18',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        width: '100%',
                        justifyContent: 'center',
                        transition: 'background 0.15s'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.background = 'rgba(74, 140, 24, 0.12)'}
                      onMouseOut={(e) => e.currentTarget.style.background = 'rgba(74, 140, 24, 0.06)'}
                    >
                      + Add New Bike
                    </button>
                  </div>
                ) : (
                  <div style={{
                    padding: '16px',
                    border: '1px solid rgba(74, 140, 24, 0.3)',
                    borderRadius: '12px',
                    background: 'rgba(74, 140, 24, 0.04)',
                    marginTop: '4px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <strong style={{ fontSize: '14px', color: '#4A8C18' }}>New Bike</strong>
                      <button
                        type="button"
                        onClick={() => { setShowNewBikeForm(false); setNewBikeData({ vin: '', brand: '', model: '', year: new Date().getFullYear() }); }}
                        style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: 'rgba(255,255,255,0.5)', padding: '0 4px' }}
                      >x</button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ fontSize: '12px', marginBottom: '4px', display: 'block', fontWeight: '600' }}>VIN *</label>
                        <input
                          type="text"
                          value={newBikeData.vin}
                          onChange={(e) => setNewBikeData({ ...newBikeData, vin: e.target.value })}
                          placeholder="Frame number / VIN"
                          style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #d1d5db', background: '#ffffff', color: '#111827', fontSize: '14px', boxSizing: 'border-box' }}
                        />
                        <p style={{ fontSize: '11px', color: 'rgba(17,24,39,0.65)', margin: '6px 0 0' }}>
                          If this frame is already registered, close here and use the search field, or press Create — we will select the existing bike when the frame matches.
                        </p>
                      </div>
                      <div>
                        <label style={{ fontSize: '12px', marginBottom: '4px', display: 'block', fontWeight: '600' }}>Brand *</label>
                        <input
                          type="text"
                          value={newBikeData.brand}
                          onChange={(e) => setNewBikeData({ ...newBikeData, brand: e.target.value })}
                          placeholder="e.g. Trek"
                          style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #d1d5db', background: '#ffffff', color: '#111827', fontSize: '14px', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '12px', marginBottom: '4px', display: 'block', fontWeight: '600' }}>Model *</label>
                        <input
                          type="text"
                          value={newBikeData.model}
                          onChange={(e) => setNewBikeData({ ...newBikeData, model: e.target.value })}
                          placeholder="e.g. Domane SL6"
                          style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #d1d5db', background: '#ffffff', color: '#111827', fontSize: '14px', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '12px', marginBottom: '4px', display: 'block', fontWeight: '600' }}>Year *</label>
                        <input
                          type="number"
                          value={newBikeData.year}
                          onChange={(e) => setNewBikeData({ ...newBikeData, year: parseInt(e.target.value) || '' })}
                          min="1900"
                          max="2030"
                          style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #d1d5db', background: '#ffffff', color: '#111827', fontSize: '14px', boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                      <button
                        type="button"
                        onClick={handleCreateNewBike}
                        disabled={creatingBike}
                        style={{
                          flex: 1,
                          padding: '10px',
                          border: 'none',
                          borderRadius: '10px',
                          background: creatingBike ? '#888' : 'linear-gradient(135deg, var(--lime-primary) 0%, #84cc16 100%)',
                          color: '#0a0a1a',
                          fontSize: '13px',
                          fontWeight: '700',
                          cursor: creatingBike ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {creatingBike ? 'Creating...' : 'Create Bike & Select'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowNewBikeForm(false); setNewBikeData({ vin: '', brand: '', model: '', year: new Date().getFullYear() }); }}
                        style={{
                          padding: '10px 16px',
                          border: '1px solid rgba(255,255,255,0.15)',
                          borderRadius: '10px',
                          background: 'transparent',
                          color: '#fff',
                          fontSize: '13px',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                {companyInfo && !showNewBikeForm && (
                  <p style={{ fontSize: '12px', color: 'var(--color-gray)', marginTop: '5px' }}>
                    All bikes belong to: <strong>{companyInfo.name}</strong>
                  </p>
                )}
              </div>

              <div className="form-group">
                <label>Service Date *</label>
                <input
                  type="date"
                  value={formData.service_date}
                  onChange={(e) => setFormData({ ...formData, service_date: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Service Time (Optional)</label>
                <input
                  type="time"
                  value={formData.service_time}
                  onChange={(e) => setFormData({ ...formData, service_time: e.target.value })}
                />
                <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>
                  Future date/time = scheduled, past/current = in progress (completed after invoicing)
                </p>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  placeholder="Service description..."
                />
              </div>

              <div className="form-group">
                <label>Total Cost *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.total_cost}
                  onChange={(e) => setFormData({ ...formData, total_cost: parseFloat(e.target.value) || 0 })}
                  required
                />
              </div>

              {/* Receipt Upload + AI Extraction (Pro/Enterprise) */}
              {companyInfo && ['professional', 'pro', 'enterprise', 'lifetime'].includes((companyInfo.subscription_tier || '').toLowerCase()) && (
                <div className="form-group">
                  <label style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icon name="receipt" color="secondary" darkMode={true} size={16} />
                    Receipt / Invoice (AI Extraction)
                  </label>
                  <ReceiptUpload
                    onFileSelect={handleReceiptSelect}
                    onFilesSelect={handleReceiptsSelect}
                    file={formData.receipt}
                    files={formData.receipts}
                    preview={receiptPreview}
                    previews={receiptPreviews}
                    extracting={extracting}
                    extractedData={extractedData}
                    onAIExtract={handleAIExtract}
                    showAIButton={true}
                    darkMode={true}
                    label="Drop receipt/invoice here or click to upload"
                    multiple={true}
                    maxFiles={5}
                  />
                  {extractedData && (
                    <div style={{
                      marginTop: '10px',
                      padding: '12px',
                      borderRadius: '10px',
                      background: 'rgba(34, 197, 94, 0.08)',
                      border: '1px solid rgba(34, 197, 94, 0.25)',
                      fontSize: '13px',
                      color: 'rgba(255,255,255,0.85)'
                    }}>
                      <strong style={{ color: '#22c55e' }}>AI extracted data applied to form.</strong>
                      {extractedData.service_items?.length > 0 && (
                        <span> {extractedData.service_items.length} item(s) added below.</span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Service Items Table - shows extracted items or manually added ones */}
              {formData.service_items && formData.service_items.length > 0 ? (
                <div className="form-group">
                  <label style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                    <span>Service Items ({formData.service_items.length})</span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        type="button"
                        onClick={() => {
                          const itemsTotal = formData.service_items.reduce((sum, item) => sum + (item.total_price || 0), 0);
                          setFormData(prev => ({ ...prev, total_cost: parseFloat(itemsTotal.toFixed(2)) }));
                        }}
                        style={{
                          background: 'rgba(34, 197, 94, 0.15)',
                          border: '1px solid rgba(34, 197, 94, 0.3)',
                          borderRadius: '6px',
                          color: '#22c55e',
                          fontSize: '11px',
                          cursor: 'pointer',
                          padding: '4px 10px',
                          fontWeight: '600'
                        }}
                      >
                        Sync total from items
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, service_items: [] }))}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'rgba(255,255,255,0.4)',
                          fontSize: '12px',
                          cursor: 'pointer',
                          textDecoration: 'underline'
                        }}
                      >
                        Clear all
                      </button>
                    </div>
                  </label>
                  <div style={{
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '10px',
                    overflow: 'hidden'
                  }}>
                    {/* Items - card layout for mobile compatibility */}
                    {formData.service_items.map((item, idx) => (
                      <div key={idx} style={{
                        padding: '12px',
                        borderTop: idx > 0 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                        background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)'
                      }}>
                        {/* Item name - full width */}
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                          <input
                            type="text"
                            value={item.item_name}
                            onChange={(e) => {
                              const updated = [...formData.service_items];
                              updated[idx] = { ...updated[idx], item_name: e.target.value };
                              setFormData(prev => ({ ...prev, service_items: updated }));
                            }}
                            placeholder="Item name"
                            style={{
                              background: 'rgba(255,255,255,0.05)',
                              border: '1px solid rgba(255,255,255,0.1)',
                              borderRadius: '6px',
                              color: '#fff',
                              padding: '8px 10px',
                              fontSize: '14px',
                              flex: 1,
                              minWidth: 0
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const updated = formData.service_items.filter((_, i) => i !== idx);
                              setFormData(prev => ({ ...prev, service_items: updated }));
                            }}
                            style={{
                              background: 'rgba(239, 68, 68, 0.1)',
                              border: '1px solid rgba(239, 68, 68, 0.2)',
                              borderRadius: '6px',
                              color: '#ef4444',
                              cursor: 'pointer',
                              fontSize: '16px',
                              padding: '6px 10px',
                              lineHeight: '1',
                              flexShrink: 0,
                              minWidth: '36px',
                              minHeight: '36px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            title="Remove item"
                          >
                            ×
                          </button>
                        </div>
                        {/* Qty, Unit Price, Total - row */}
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginBottom: '3px', textTransform: 'uppercase', fontWeight: '600' }}>Qty</div>
                            <input
                              type="number"
                              value={item.quantity}
                              min="1"
                              step="1"
                              onChange={(e) => {
                                const updated = [...formData.service_items];
                                const qty = parseFloat(e.target.value) || 1;
                                updated[idx] = { ...updated[idx], quantity: qty, total_price: qty * updated[idx].unit_price };
                                setFormData(prev => ({ ...prev, service_items: updated }));
                              }}
                              style={{
                                background: 'rgba(255,255,255,0.05)',
                                border: '1px solid rgba(255,255,255,0.1)',
                                borderRadius: '6px',
                                color: '#fff',
                                padding: '8px 6px',
                                fontSize: '14px',
                                textAlign: 'center',
                                width: '100%'
                              }}
                            />
                          </div>
                          <div style={{ flex: 1.5, minWidth: 0 }}>
                            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginBottom: '3px', textTransform: 'uppercase', fontWeight: '600' }}>Unit Price</div>
                            <input
                              type="number"
                              step="0.01"
                              value={item.unit_price}
                              onChange={(e) => {
                                const updated = [...formData.service_items];
                                const price = parseFloat(e.target.value) || 0;
                                updated[idx] = { ...updated[idx], unit_price: price, total_price: updated[idx].quantity * price };
                                setFormData(prev => ({ ...prev, service_items: updated }));
                              }}
                              style={{
                                background: 'rgba(255,255,255,0.05)',
                                border: '1px solid rgba(255,255,255,0.1)',
                                borderRadius: '6px',
                                color: '#fff',
                                padding: '8px 6px',
                                fontSize: '14px',
                                textAlign: 'right',
                                width: '100%'
                              }}
                            />
                          </div>
                          <div style={{ flex: 1.5, minWidth: 0 }}>
                            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginBottom: '3px', textTransform: 'uppercase', fontWeight: '600' }}>Total</div>
                            <div style={{
                              padding: '8px 6px',
                              fontSize: '14px',
                              fontWeight: '700',
                              color: '#22c55e',
                              textAlign: 'right'
                            }}>
                              {item.total_price?.toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    {/* Total row */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px',
                      borderTop: '1px solid rgba(255,255,255,0.15)',
                      background: 'rgba(34, 197, 94, 0.08)',
                      fontSize: '15px',
                      fontWeight: '700'
                    }}>
                      <span style={{ color: 'rgba(255,255,255,0.7)' }}>Items Total</span>
                      <span style={{ color: '#22c55e', fontSize: '17px' }}>
                        {formData.service_items.reduce((sum, item) => sum + (item.total_price || 0), 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        service_items: [...prev.service_items, { item_name: '', quantity: 1, unit_price: 0, total_price: 0 }]
                      }));
                    }}
                    style={{
                      marginTop: '8px',
                      background: 'none',
                      border: '1px dashed rgba(255,255,255,0.15)',
                      borderRadius: '8px',
                      color: 'rgba(255,255,255,0.5)',
                      padding: '10px 12px',
                      fontSize: '13px',
                      cursor: 'pointer',
                      width: '100%',
                      minHeight: '44px'
                    }}
                  >
                    + Add item manually
                  </button>
                </div>
              ) : (
                <div className="form-group">
                  <button
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        service_items: [{ item_name: '', quantity: 1, unit_price: 0, total_price: 0 }]
                      }));
                    }}
                    style={{
                      background: 'none',
                      border: '1px dashed rgba(255,255,255,0.15)',
                      borderRadius: '8px',
                      color: 'rgba(255,255,255,0.5)',
                      padding: '10px 12px',
                      fontSize: '13px',
                      cursor: 'pointer',
                      width: '100%',
                      minHeight: '44px'
                    }}
                  >
                    + Add items manually
                  </button>
                </div>
              )}

              {/* Message for Basic users */}
              {companyInfo && companyInfo.subscription_tier === 'basic' && (
                <div style={{
                  padding: '16px',
                  background: 'rgba(255, 193, 7, 0.1)',
                  border: '1px solid rgba(255, 193, 7, 0.3)',
                  borderRadius: '12px',
                  marginBottom: '16px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <Icon name="lock" size={18} color="warning" darkMode={true} />
                    <span style={{ fontWeight: '600', color: '#f59e0b' }}>Receipt Upload & AI Extraction</span>
                  </div>
                  <p style={{ margin: 0, fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>
                    Upgrade to Professional to upload receipts and use AI to automatically extract service details, items, and prices.
                  </p>
                </div>
              )}


              <div className="modal-actions" style={{
                display: 'flex',
                gap: '12px',
                flexDirection: 'row',
                flexWrap: 'wrap'
              }}>
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ flex: 1, minWidth: '120px', minHeight: '48px' }}
                >
                  Add Service
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => { setShowAddModal(false); resetAddForm(); }}
                  style={{ flex: 1, minWidth: '120px', minHeight: '48px' }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <SquareTicketModal
        isOpen={showSquareTicketModal}
        onClose={() => { setShowSquareTicketModal(false); setTicketServiceContext(null); fetchData(); }}
        onSaved={handleSquareTicketSaved}
        darkMode={true}
        serviceContext={ticketServiceContext}
      />

      <SquarePaymentModal
        isOpen={showSquarePaymentModal}
        paymentContext={squarePaymentContext}
        onClose={() => { setShowSquarePaymentModal(false); setSquarePaymentContext(null); fetchData(); }}
        onPaid={handleSquarePaymentDone}
        darkMode={true}
      />

      {/* Comments Modal */}
      {showCommentsModal && (
        <div className="modal-overlay" onClick={() => setShowCommentsModal(null)}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icon name="chat" color="primary" darkMode={true} size={20} />
                Service Comments
              </h2>
              <button className="close-btn" onClick={() => setShowCommentsModal(null)}>×</button>
            </div>

            <div style={{ padding: '20px' }}>
              {/* Info box about locked services */}
              <div style={{
                padding: '12px 16px',
                backgroundColor: 'rgba(255, 193, 7, 0.1)',
                border: '1px solid rgba(255, 193, 7, 0.3)',
                borderRadius: '8px',
                marginBottom: '16px',
                fontSize: '13px',
                color: '#ff9800',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px'
              }}>
                <Icon name="lock" color="warning" darkMode={true} size={16} style={{ marginTop: '2px', flexShrink: 0 }} />
                <span><strong>Service is locked.</strong> The description and price cannot be modified afterwards, but you can add comments anytime.</span>
              </div>

              {/* Add comment form */}
              <form onSubmit={handleAddComment} style={{ marginBottom: '20px' }}>
                <div className="form-group" style={{ marginBottom: '12px' }}>
                  <label>New Comment</label>
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Write a comment for the service..."
                    rows={3}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'var(--bg-overlay-medium)', color: '#ffffff', fontSize: '14px' }}
                    required
                  />
                </div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <select
                    value={commentCategory}
                    onChange={(e) => setCommentCategory(e.target.value)}
                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.1)', background: 'var(--bg-overlay-medium)', color: '#ffffff', fontSize: '13px' }}
                  >
                    <option value="general">General</option>
                    <option value="follow_up">Follow-up note</option>
                    <option value="issue">Issue</option>
                    <option value="note">Note</option>
                    <option value="question">Question</option>
                    <option value="resolution">Resolution</option>
                  </select>
                  <button type="submit" className="btn btn-primary" style={{ padding: '8px 16px' }}>
                    Add Comment
                  </button>
                </div>
              </form>

              {/* Comments list */}
              <div>
                <h4 style={{ marginBottom: '12px', color: '#ffffff' }}>Previous Comments ({comments.length})</h4>
                {loadingComments ? (
                  <p style={{ color: 'var(--color-gray-light)' }}>Loading...</p>
                ) : comments.length === 0 ? (
                  <p style={{ color: 'var(--color-gray-light)', fontStyle: 'italic' }}>No comments yet for this service.</p>
                ) : (
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {comments.map(comment => (
                      <div key={comment.id} style={{
                        padding: '12px',
                        marginBottom: '10px',
                        backgroundColor: 'rgba(255,255,255,0.03)',
                        borderRadius: '8px',
                        border: '1px solid rgba(255,255,255,0.08)'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <div>
                            <strong style={{ color: '#3d5a20' }}>{comment.author_name || comment.author_username}</strong>
                            <span style={{
                              marginLeft: '8px',
                              padding: '2px 6px',
                              backgroundColor: 'rgba(61, 90, 32, 0.1)',
                              borderRadius: '4px',
                              fontSize: '11px',
                              color: '#3d5a20'
                            }}>
                              {comment.author_role}
                            </span>
                            <span style={{
                              marginLeft: '8px',
                              padding: '2px 6px',
                              backgroundColor: 'rgba(158, 158, 158, 0.2)',
                              borderRadius: '4px',
                              fontSize: '11px',
                              color: '#9e9e9e'
                            }}>
                              {comment.category || 'general'}
                            </span>
                          </div>
                          <span style={{ fontSize: '12px', color: 'var(--color-gray-light)' }}>
                            {new Date(comment.created_at).toLocaleString('en-GB')}
                          </span>
                        </div>
                        <p style={{ margin: 0, color: 'rgba(255,255,255,0.9)', whiteSpace: 'pre-wrap' }}>{comment.comment_text}</p>
                        {comment.edit_history && comment.edit_history.length > 0 && (
                          <span style={{ fontSize: '11px', color: 'var(--color-gray-light)', fontStyle: 'italic' }}>
                            (edited: {new Date(comment.updated_at).toLocaleString('en-GB')})
                          </span>
                        )}
                        <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => handleDeleteComment(comment.id)}
                            className="btn btn-sm"
                            style={{ padding: '2px 8px', fontSize: '11px', backgroundColor: 'rgba(244, 67, 54, 0.2)', color: '#f44336' }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Details Modal */}
      {showUserModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            padding: '24px'
          }}
          onClick={() => {
            setShowUserModal(false);
            setSelectedUser(null);
          }}
        >
          <div
            style={{
              background: '#1a1a3e',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '20px',
              padding: '28px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: 'white' }}><Icon name="profile" color="secondary" darkMode={true} size={20} style={{ verticalAlign: 'middle', marginRight: '8px' }} /> User Details</h2>
              <button
                onClick={() => {
                  setShowUserModal(false);
                  setSelectedUser(null);
                }}
                style={{ background: 'rgba(255,255,255,0.05)', border: 'none', fontSize: '20px', cursor: 'pointer', padding: '8px 12px', borderRadius: '8px', color: 'rgba(255,255,255,0.6)' }}
              >x</button>
            </div>

            {loadingUser ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.6)' }}>Loading...</div>
            ) : selectedUser ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ padding: '16px', background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }}>
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>Name</div>
                  <div style={{ fontSize: '16px', color: 'white', fontWeight: '600' }}>
                    {selectedUser.firstname} {selectedUser.surname}
                  </div>
                </div>

                <div style={{ padding: '16px', background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }}>
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>Username</div>
                  <div style={{ fontSize: '16px', color: 'white' }}>{selectedUser.username || 'N/A'}</div>
                </div>

                <div style={{ padding: '16px', background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }}>
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>Email</div>
                  <div style={{ fontSize: '16px', color: 'white' }}>{selectedUser.email || 'N/A'}</div>
                </div>

                <div style={{ padding: '16px', background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }}>
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>Phone</div>
                  <div style={{ fontSize: '16px', color: 'white' }}>
                    {selectedUser.phone ? (
                      <a href={`tel:${selectedUser.phone}`} style={{ color: '#2196f3', textDecoration: 'none' }}>
                        {selectedUser.phone}
                      </a>
                    ) : (
                      'N/A'
                    )}
                  </div>
                </div>

                {selectedUser.company_name && (
                  <div style={{ padding: '16px', background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }}>
                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>Company</div>
                    <div style={{ fontSize: '16px', color: 'white' }}>{selectedUser.company_name}</div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.6)' }}>No user data available</div>
            )}
          </div>
        </div>
      )}

      {/* Service details modal */}
      {showServiceDetailModal && selectedService && (
        <div className="modal-overlay" onClick={() => setShowServiceDetailModal(false)}>
          <div
            className="modal"
            style={{
              maxWidth: '640px',
              background: darkMode ? '#141414' : '#ffffff',
              color: darkMode ? '#f5f5f5' : '#121212',
              border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(15,23,42,0.12)',
              boxShadow: darkMode
                ? '0 24px 80px rgba(0,0,0,0.45)'
                : '0 24px 80px rgba(15,23,42,0.18)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="modal-header"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(15,23,42,0.1)'
              }}
            >
              <h2 style={{ color: darkMode ? '#ffffff' : '#111827' }}>Service details</h2>
              <button
                className="close-btn"
                onClick={() => setShowServiceDetailModal(false)}
                style={{ color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(17,24,39,0.7)' }}
              >
                x
              </button>
            </div>
            <div style={{ padding: '20px 24px 24px' }}>
              <div style={{ display: 'grid', gap: '12px' }}>
                <div>
                  <strong style={{ color: darkMode ? 'rgba(255,255,255,0.62)' : 'rgba(17,24,39,0.62)', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Date</strong><br />
                  {detailEditMode ? (
                    <input
                      type="date"
                      value={detailEditValues.service_date || ''}
                      onChange={(e) => setDetailEditValues(v => ({ ...v, service_date: e.target.value }))}
                      style={{
                        marginTop: '4px', padding: '8px 10px', borderRadius: '8px',
                        border: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                        background: darkMode ? 'rgba(255,255,255,0.05)' : '#fff',
                        color: darkMode ? '#f8fafc' : '#111827', fontSize: '14px', width: '100%', maxWidth: '200px'
                      }}
                    />
                  ) : (
                    <span style={{ color: darkMode ? '#f8fafc' : '#111827', fontSize: '16px' }}>{formatDetailDate(selectedService.service_date)}</span>
                  )}
                </div>
                <div style={{ padding: '14px', borderRadius: '12px', border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(15,23,42,0.1)', background: darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(248,250,252,0.9)' }}>
                  <strong style={{ color: darkMode ? 'rgba(255,255,255,0.62)' : 'rgba(17,24,39,0.62)', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Bike</strong><br />
                  {selectedService.bike_id ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                      <span style={{ color: darkMode ? 'rgba(255,255,255,0.9)' : '#111827' }}>
                        {selectedService.bike_brand || 'N/A'} {selectedService.bike_model || ''} - {selectedService.bike_vin}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setBikePassportContext({
                            id: selectedService.bike_id,
                            brand: selectedService.bike_brand,
                            model: selectedService.bike_model,
                          });
                          setShowBikePassportModal(true);
                        }}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight: 600,
                          background: darkMode ? 'rgba(78, 205, 196, 0.18)' : 'rgba(15, 118, 110, 0.12)',
                          color: darkMode ? '#4ecdc4' : '#0f766e',
                          border: darkMode ? '1px solid rgba(78, 205, 196, 0.4)' : '1px solid rgba(15, 118, 110, 0.3)',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        <Icon name="badge" color="primary" darkMode={darkMode} size={14} />
                        Bike Passport
                      </button>
                    </div>
                  ) : (
                    `${selectedService.bike_brand || 'N/A'} ${selectedService.bike_model || ''} - ${selectedService.bike_vin}`
                  )}
                </div>
                {isSquarePlaceholderVin(selectedService.bike_vin) && (
                  <div style={{ padding: '14px', borderRadius: '12px', border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(15,23,42,0.1)', background: darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(248,250,252,0.9)' }}>
                    <strong style={{ color: darkMode ? 'rgba(255,255,255,0.62)' : 'rgba(17,24,39,0.62)', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      Link to Existing Bike Passport
                    </strong>
                    <input
                      type="text"
                      className="bike-search-input"
                      inputMode="search"
                      autoComplete="off"
                      autoCorrect="off"
                      spellCheck={false}
                      value={serviceRelinkBikeSearch}
                      onChange={(e) => {
                        setServiceRelinkBikeSearch(e.target.value);
                        setServiceRelinkBikePage(1);
                      }}
                      placeholder="Search bike passport by frame number, brand or model"
                      style={{ marginTop: '8px', marginBottom: '8px' }}
                    />
                    <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <select
                        value={serviceRelinkBikeId}
                        onChange={(e) => setServiceRelinkBikeId(e.target.value)}
                        style={{
                          flex: '1 1 260px',
                          minWidth: '240px',
                          padding: '9px 10px',
                          borderRadius: '8px',
                          border: darkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(15,23,42,0.15)',
                          background: darkMode ? 'rgba(10,10,26,0.9)' : '#ffffff',
                          color: darkMode ? '#f8fafc' : '#111827'
                        }}
                      >
                        <option value="">Select bike passport</option>
                        {bikes
                          .filter((bike) => !isSquarePlaceholderVin(bike.vin))
                          .map((bike) => (
                            <option key={bike.id} value={bike.id}>
                              {bike.vin} - {bike.brand} {bike.model}
                            </option>
                          ))}
                      </select>
                      <button
                        type="button"
                        onClick={handleRelinkServiceBike}
                        disabled={relinkingServiceBike || !serviceRelinkBikeId}
                        style={{
                          padding: '9px 12px',
                          borderRadius: '8px',
                          border: 'none',
                          cursor: relinkingServiceBike || !serviceRelinkBikeId ? 'not-allowed' : 'pointer',
                          background: 'linear-gradient(135deg, #1A2811 0%, #4A8C18 100%)',
                          color: '#ffffff',
                          fontWeight: '600',
                          opacity: relinkingServiceBike || !serviceRelinkBikeId ? 0.65 : 1
                        }}
                      >
                        {relinkingServiceBike ? 'Linking...' : 'Link Bike Passport'}
                      </button>
                    </div>
                    <div style={{
                      marginTop: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '8px',
                      flexWrap: 'wrap',
                      fontSize: '12px',
                      color: darkMode ? 'rgba(255,255,255,0.62)' : 'rgba(17,24,39,0.62)'
                    }}>
                      <span>
                        {loadingBikeOptions ? 'Loading bikes...' : (bikeOptionsTotal > 0 ? `Showing ${((serviceRelinkBikePage - 1) * 20) + 1}-${Math.min(serviceRelinkBikePage * 20, bikeOptionsTotal)} of ${bikeOptionsTotal}` : 'No bike passports found')}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => setServiceRelinkBikePage(Math.max(1, serviceRelinkBikePage - 1))}
                          disabled={serviceRelinkBikePage <= 1 || loadingBikeOptions}
                        >
                          Prev
                        </button>
                        <span>Page {serviceRelinkBikePage}/{bikeOptionsPages}</span>
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => setServiceRelinkBikePage(Math.min(bikeOptionsPages, serviceRelinkBikePage + 1))}
                          disabled={serviceRelinkBikePage >= bikeOptionsPages || loadingBikeOptions}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                <div>
                  <strong style={{ color: darkMode ? 'rgba(255,255,255,0.62)' : 'rgba(17,24,39,0.62)', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Status</strong><br />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginTop: '4px' }}>
                    <span style={{
                      display: 'inline-flex',
                      padding: '3px 10px',
                      borderRadius: '999px',
                      fontSize: '12px',
                      textTransform: 'capitalize',
                      backgroundColor: getServiceStatusStyle(selectedService.status).backgroundColor,
                      color: getServiceStatusStyle(selectedService.status).color
                    }}>
                      {formatServiceStatus(selectedService.status)}
                    </span>
                    {['ready_for_billing', 'pending_payment'].includes((selectedService.status || '').toLowerCase()) && !selectedService.square_session?.square_order_id && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        style={{ fontSize: '12px', padding: '6px 12px' }}
                        onClick={async () => {
                          try {
                            await adminAPI.updateService(selectedService.id, { status: 'completed' });
                            setSuccess('Service marked as completed');
                            fetchData();
                            const updated = await loadServiceById(selectedService.id);
                            if (updated) setSelectedService(updated);
                            setTimeout(() => setSuccess(''), 3000);
                          } catch (err) {
                            setError(err.response?.data?.error || 'Failed to update status');
                            setTimeout(() => setError(''), 4000);
                          }
                        }}
                      >
                        Mark as completed
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <strong style={{ color: darkMode ? 'rgba(255,255,255,0.62)' : 'rgba(17,24,39,0.62)', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Description</strong><br />
                  {detailEditMode ? (
                    <textarea
                      value={detailEditValues.description || ''}
                      onChange={(e) => setDetailEditValues(v => ({ ...v, description: e.target.value }))}
                      rows={3}
                      style={{
                        marginTop: '4px', padding: '8px 10px', borderRadius: '8px', width: '100%',
                        border: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                        background: darkMode ? 'rgba(255,255,255,0.05)' : '#fff',
                        color: darkMode ? '#f8fafc' : '#111827', fontSize: '14px', resize: 'vertical'
                      }}
                    />
                  ) : (
                    <span style={{ color: darkMode ? '#f8fafc' : '#111827' }}>{selectedService.description || 'N/A'}</span>
                  )}
                </div>
                <div>
                  <strong style={{ color: darkMode ? 'rgba(255,255,255,0.62)' : 'rgba(17,24,39,0.62)', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Total cost</strong><br />
                  {detailEditMode ? (
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={detailEditValues.total_cost ?? ''}
                      onChange={(e) => setDetailEditValues(v => ({ ...v, total_cost: parseFloat(e.target.value) || 0 }))}
                      style={{
                        marginTop: '4px', padding: '8px 10px', borderRadius: '8px',
                        border: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                        background: darkMode ? 'rgba(255,255,255,0.05)' : '#fff',
                        color: darkMode ? '#f8fafc' : '#111827', fontSize: '14px', width: '120px'
                      }}
                    />
                  ) : (
                    <span style={{ color: darkMode ? '#f8fafc' : '#111827' }}>GBP {selectedService.total_cost?.toFixed(2) || '0.00'}</span>
                  )}
                </div>
                {['scheduled', 'in_progress', 'ready_for_billing', 'pending_payment'].includes((selectedService.status || '').toLowerCase()) && (
                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                    {detailEditMode ? (
                      <>
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={detailEditSaving}
                          onClick={async () => {
                            setDetailEditSaving(true);
                            try {
                              await adminAPI.updateService(selectedService.id, {
                                description: detailEditValues.description,
                                total_cost: detailEditValues.total_cost,
                                service_date: detailEditValues.service_date
                              });
                              setSuccess('Service updated');
                              setDetailEditMode(false);
                              fetchData();
                              const updated = await loadServiceById(selectedService.id);
                              if (updated) setSelectedService(updated);
                              setTimeout(() => setSuccess(''), 3000);
                            } catch (err) {
                              setError(err.response?.data?.error || 'Failed to update service');
                              setTimeout(() => setError(''), 4000);
                            } finally {
                              setDetailEditSaving(false);
                            }
                          }}
                        >
                          {detailEditSaving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          disabled={detailEditSaving}
                          onClick={() => {
                            setDetailEditMode(false);
                            const sd = selectedService.service_date;
                            const ds = !sd ? '' : typeof sd === 'string' ? sd.split('T')[0] : (sd instanceof Date ? sd.toISOString().split('T')[0] : String(sd).split('T')[0]);
                            setDetailEditValues({ description: selectedService.description || '', total_cost: selectedService.total_cost ?? 0, service_date: ds });
                          }}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                          setDetailEditMode(true);
                          const sd = selectedService.service_date;
                          const ds = !sd ? '' : typeof sd === 'string' ? sd.split('T')[0] : (sd instanceof Date ? sd.toISOString().split('T')[0] : String(sd).split('T')[0]);
                          setDetailEditValues({ description: selectedService.description || '', total_cost: selectedService.total_cost ?? 0, service_date: ds });
                        }}
                      >
                        <Icon name="edit" color="secondary" darkMode={darkMode} size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                        Edit
                      </button>
                    )}
                  </div>
                )}
                {selectedService.square_session && (
                  <div>
                    <strong style={{ color: darkMode ? 'rgba(255,255,255,0.62)' : 'rgba(17,24,39,0.62)', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Square billing source</strong>
                    <div style={{
                      marginTop: '6px',
                      padding: '12px',
                      borderRadius: '12px',
                      border: darkMode ? '1px solid rgba(96, 165, 250, 0.3)' : '1px solid rgba(59, 130, 246, 0.28)',
                      background: darkMode ? 'rgba(59, 130, 246, 0.08)' : 'rgba(219, 234, 254, 0.85)',
                      display: 'grid',
                      gap: '6px',
                      fontSize: '13px',
                      color: darkMode ? 'rgba(255,255,255,0.92)' : '#0f172a'
                    }}>
                      <div>
                        <strong style={{ color: darkMode ? '#60a5fa' : '#1d4ed8' }}>Session ID:</strong> {selectedService.square_session.session_id || 'N/A'}
                      </div>
                      <div style={{ wordBreak: 'break-all' }}>
                        <strong style={{ color: darkMode ? '#60a5fa' : '#1d4ed8' }}>Order ID:</strong> {selectedService.square_session.square_order_id || 'N/A'}
                      </div>
                      <div>
                        <strong style={{ color: darkMode ? '#60a5fa' : '#1d4ed8' }}>Statuses:</strong> {formatSquareStatus(selectedService.square_session.order_status)} / {formatSquareStatus(selectedService.square_session.payment_status)}
                      </div>
                      <div>
                        <strong style={{ color: darkMode ? '#60a5fa' : '#1d4ed8' }}>Amount:</strong> {formatSquareAmount(selectedService.square_session.total_amount_cents, selectedService.square_session.currency)}
                      </div>
                      <div>
                        <strong style={{ color: darkMode ? '#60a5fa' : '#1d4ed8' }}>Created:</strong> {formatDetailDateTime(selectedService.square_session.created_at)}
                      </div>
                      <div>
                        <strong style={{ color: darkMode ? '#60a5fa' : '#1d4ed8' }}>Updated:</strong> {formatDetailDateTime(selectedService.square_session.updated_at)}
                      </div>
                      <div>
                        <strong style={{ color: darkMode ? '#60a5fa' : '#1d4ed8' }}>Completed:</strong> {formatDetailDateTime(selectedService.square_session.completed_at)}
                      </div>
                      {Array.isArray(selectedService.square_session.payment_ids) && selectedService.square_session.payment_ids.length > 0 && (
                        <div style={{ wordBreak: 'break-all' }}>
                          <strong style={{ color: darkMode ? '#60a5fa' : '#1d4ed8' }}>Payment IDs:</strong> {selectedService.square_session.payment_ids.join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {selectedService.items && selectedService.items.length > 0 && (
                  <div>
                    <strong style={{ color: darkMode ? 'rgba(255,255,255,0.62)' : 'rgba(17,24,39,0.62)', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Items</strong>
                    <ul style={{ margin: '4px 0 0', paddingLeft: '20px' }}>
                      {selectedService.items.map((item, i) => (
                        <li key={i}>{item.item_name || item.description} - GBP {(item.total_price || item.unit_price || 0).toFixed(2)}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div>
                  <strong style={{ color: darkMode ? 'rgba(255,255,255,0.62)' : 'rgba(17,24,39,0.62)', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Receipts</strong>
                  <div style={{ marginTop: '6px' }}>
                    {selectedService.receipts && selectedService.receipts.length > 0 ? (
                      selectedService.receipts.map((r, idx) => (
                        r.file_url ? (
                          <a key={r.id || idx} href={r.file_url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', color: darkMode ? '#60a5fa' : '#2563eb', marginBottom: '4px' }}>
                            {r.original_filename || r.filename || `Receipt ${idx + 1}`}
                          </a>
                        ) : (
                          <span key={r.id || idx} style={{ display: 'block', marginBottom: '4px' }}>Receipt {idx + 1}</span>
                        )
                      ))
                    ) : (
                      <span style={{ color: darkMode ? 'rgba(255,255,255,0.62)' : 'rgba(17,24,39,0.62)' }}>No receipts yet</span>
                    )}
                  </div>
                  {companyInfo && ['professional', 'pro', 'enterprise', 'lifetime'].includes((companyInfo.subscription_tier || '').toLowerCase()) && (
                    <div style={{ marginTop: '10px' }}>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        style={{ fontSize: '12px', padding: '8px 14px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                        onClick={() => {
                          setShowDetailReceipt(!showDetailReceipt);
                          if (showDetailReceipt) {
                            setDetailReceiptFile(null);
                            setDetailReceiptFiles([]);
                            setDetailReceiptPreview(null);
                            setDetailReceiptPreviews([]);
                            setDetailScanExtracted(null);
                          }
                        }}
                      >
                        <Icon name={showDetailReceipt ? 'close' : 'receipt'} color="secondary" darkMode={darkMode} size={14} />
                        {showDetailReceipt ? 'Cancel' : 'Upload receipt'}
                      </button>
                    </div>
                  )}
                  {showDetailReceipt && (
                    <div style={{ marginTop: '12px' }}>
                      <ReceiptUpload
                        onFileSelect={(f) => { setDetailReceiptFile(f); setDetailReceiptFiles([]); setDetailScanExtracted(null); setDetailReceiptPreviews([]); }}
                        onFilesSelect={(fs) => { setDetailReceiptFiles(fs); setDetailReceiptFile(null); setDetailScanExtracted(null); setDetailReceiptPreview(null); }}
                        file={detailReceiptFile}
                        files={detailReceiptFiles}
                        preview={detailReceiptPreview}
                        previews={detailReceiptPreviews}
                        extracting={detailScanExtracting}
                        extractedData={detailScanExtracted}
                        onAIExtract={handleDetailAIExtract}
                        showAIButton={true}
                        darkMode={darkMode}
                        label="Receipt image"
                        multiple={true}
                        maxFiles={5}
                      />
                      {(detailReceiptFile || detailReceiptFiles.length > 0) && (
                        <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            className="btn btn-primary"
                            style={{ fontSize: '13px', padding: '10px 18px' }}
                            onClick={handleDetailReceiptSave}
                          >
                            <Icon name="upload" customColor="#ffffff" size={14} style={{ marginRight: '6px' }} />
                            {detailScanExtracted ? 'Save receipt & apply data' : 'Upload receipt'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {squareTerminalMode && selectedService.status !== 'completed' && selectedService.status !== 'cancelled' && (selectedService.status === 'ready_for_billing' || selectedService.status === 'pending_payment') && selectedService.square_session?.square_order_id && (
                  <div style={{ paddingTop: '8px' }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowServiceDetailModal(false);
                        openPaymentModal(selectedService);
                      }}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                    >
                      <Icon name="payment" color="info" darkMode={darkMode} size={16} />
                      Process payment
                    </button>
                  </div>
                )}
                {squareTerminalMode && selectedService.status === 'pending_payment' && (
                  <div style={{ paddingTop: '8px' }}>
                    <button
                      type="button"
                      className="btn"
                      onClick={async () => {
                        try {
                          setError('');
                          const res = await squareAPI.createCheckoutLinkForService(selectedService.id);
                          const data = res?.data || res || {};
                          if (data.already_paid) {
                            setSuccess('This order was already paid in Square! Refreshing...');
                            setShowServiceDetailModal(false);
                            fetchData();
                            setTimeout(() => setSuccess(''), 3000);
                            return;
                          }
                          if (data.url) {
                            window.open(data.url, '_blank');
                          } else {
                            setError('Checkout link was created but no URL was returned.');
                          }
                        } catch (err) {
                          setError(err.response?.data?.error || 'Failed to create checkout link');
                          setTimeout(() => setError(''), 4000);
                        }
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        backgroundColor: darkMode ? 'rgba(245, 158, 11, 0.25)' : 'rgba(245, 158, 11, 0.18)',
                        color: darkMode ? '#f59e0b' : '#b45309',
                        fontWeight: 600,
                        border: darkMode ? '1px solid rgba(245, 158, 11, 0.4)' : '1px solid rgba(180, 83, 9, 0.35)',
                      }}
                    >
                      <Icon name="payment" color="warning" darkMode={darkMode} size={16} />
                      Checkout with Square
                    </button>
                  </div>
                )}
                {/* Send "bike ready" email to customer - completed services only, once per service */}
                {selectedService.status === 'completed' && selectedService.user_email && !selectedService.completion_notification_sent_at && (
                  <div style={{ paddingTop: '12px', borderTop: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(15,23,42,0.12)', marginTop: '12px' }}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={sendingCompletionEmail}
                      onClick={async () => {
                        try {
                          setError('');
                          setSendingCompletionEmail(true);
                          await adminAPI.sendServiceCompletionNotification(selectedService.id);
                          setSuccess('Notification email sent to customer');
                          setSelectedService(s => s ? { ...s, completion_notification_sent_at: new Date().toISOString() } : null);
                          setTimeout(() => setSuccess(''), 4000);
                        } catch (err) {
                          setError(err.response?.data?.error || 'Failed to send email');
                          setTimeout(() => setError(''), 4000);
                        } finally {
                          setSendingCompletionEmail(false);
                        }
                      }}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                    >
                      <Icon name="email" color="primary" darkMode={darkMode} size={16} />
                      {sendingCompletionEmail ? 'Sending...' : 'Send "bike ready" email to customer'}
                    </button>
                  </div>
                )}
                {selectedService.status === 'completed' && selectedService.completion_notification_sent_at && (
                  <div style={{ paddingTop: '12px', marginTop: '12px', fontSize: '13px', color: 'rgba(34, 197, 94, 0.9)' }}>
                    <Icon name="success" customColor="rgba(34, 197, 94)" size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                    Notification sent {new Date(selectedService.completion_notification_sent_at).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bike Passport Modal - full bike page in popup (same as user view) */}
      {showBikePassportModal && bikePassportContext && user && (
        <BikePassportModal
          bikeId={bikePassportContext.id}
          bikeLabel={[bikePassportContext.brand, bikePassportContext.model].filter(Boolean).join(' ') || undefined}
          onClose={() => { setShowBikePassportModal(false); setBikePassportContext(null); }}
          darkMode={darkMode}
          user={user}
          onLogout={onLogout}
          onToggleDarkMode={onToggleDarkMode}
        />
      )}
    </div>
  );
}

export default AdminServices;
